import { Content, Editor, useEditorState } from '@tiptap/react'
import {
  KeyboardEventHandler,
  MouseEventHandler,
  useState,
  useMemo,
} from 'react'
import { Button, ButtonSize, Icon } from '@opallabs/lapidary'
import { DocumentResourceType } from '../../CollaborationTextEditor/types'
import { getMentions } from '../../CollaborationTextEditor/utils'
import { TextEditor, useTextEditor } from '../../TextEditor'
import { TextEditorContent } from '../../TextEditor/content'
import { mergeExtensions } from '../../TextEditor/hooks'
import { ContentChain, getContentChain } from '../../TextEditor/utils'
import { createMentionListRenderer } from '../MentionList'
import * as styles from './styles'

interface CommentEditorProps {
  autofocus?: boolean
  initialValue?: Content
  placeholder?: string
  threadResourceType?: DocumentResourceType
  threadResourceId?: string
  onEscape?: KeyboardEventHandler<Element>
  onSubmit: (content: ContentChain) => void
  onMentionListOpenChange?: (open: boolean) => void
}

export const CommentEditor = ({
  autofocus = false,
  placeholder,
  initialValue,
  threadResourceType,
  threadResourceId,
  onEscape,
  onSubmit,
  onMentionListOpenChange,
}: CommentEditorProps) => {
  const { editor: parentEditor } = useTextEditor()
  const [editor, setEditor] = useState<Editor | null>(null)
  const { isActive, isEmpty } =
    useEditorState({
      editor,
      selector: (snapshot) => ({
        isEmpty: snapshot.editor?.isEmpty,
        isActive: {
          bold: snapshot.editor?.isActive('bold'),
          italic: snapshot.editor?.isActive('italic'),
          underline: snapshot.editor?.isActive('underline'),
        },
      }),
    }) ?? {}

  const handleBold = () => {
    editor?.chain().focus().toggleBold().run()
  }

  const handleItalic = () => {
    editor?.chain().focus().toggleItalic().run()
  }

  const handleUnderline = () => {
    editor?.chain().focus().toggleUnderline().run()
  }

  const handleSaveContent: MouseEventHandler = () => {
    if (editor) {
      handleSubmit(getContentChain(editor))
    }
  }

  const handleSubmit = (content: ContentChain) => {
    if (editor) {
      onSubmit(content)
      editor.commands.clearContent(true)
    }
  }

  const mentionListRenderer = useMemo(
    () => createMentionListRenderer({ onMentionListOpenChange, parentEditor }),
    [onMentionListOpenChange, parentEditor]
  )

  return (
    <TextEditor
      initialValue={initialValue}
      autofocus={autofocus}
      contentStyle={[styles.content()]}
      placeholderText={placeholder}
      onEditorUpdate={(editor) => setEditor(editor)}
      onEscape={onEscape}
      onSaveContent={handleSubmit}
      extensions={mergeExtensions({
        mention: {
          config: {
            suggestion: {
              allowSpaces: true,
              items: getMentions(threadResourceType, threadResourceId),
              render: mentionListRenderer,
            },
          },
        },
      })}
    >
      <TextEditorContent className="editor-thread-comment" enableSaveShortcut />
      <div css={styles.footerActions}>
        <div css={styles.buttonGroup}>
          <Button
            css={styles.button}
            name="embolden selection"
            variant="ghost"
            size={ButtonSize.Small}
            ariaPressed={isActive?.bold}
            icon={<Icon glyph="gem-bold" />}
            onClick={handleBold}
          />
          <Button
            css={styles.button}
            name="italicize selection"
            variant="ghost"
            size={ButtonSize.Small}
            ariaPressed={isActive?.italic}
            icon={<Icon glyph="gem-italic" />}
            onClick={handleItalic}
          />
          <Button
            css={styles.button}
            name="underline selection"
            variant="ghost"
            size={ButtonSize.Small}
            ariaPressed={isActive?.underline}
            icon={<Icon glyph="gem-underline" />}
            onClick={handleUnderline}
          />
        </div>

        <Button
          disabled={!editor || isEmpty}
          name="submit comment"
          variant="secondary"
          icon={<Icon glyph="gem-submit" />}
          size={ButtonSize.Small}
          onClick={handleSaveContent}
        />
      </div>
    </TextEditor>
  )
}
